import React, { useState, useEffect, useRef } from 'react'
import { 
  Card, 
  CardBody, 
  Avatar, 
  Button, 
  Tabs, 
  Tab, 
  Chip,
  Input,
  Textarea,
  Select,
  SelectItem
} from '@heroui/react'
import { doc, updateDoc, runTransaction } from 'firebase/firestore'
import { db } from '../../Firebase/firebase'
import useAuth from '../Helpers/useAuth'
import RenderizadorDiseño, { diseñosDisponibles } from './DiseñosEnlaces'
import ContenedorEnlace from './ContenedorEnlace'

const EdicionPerfilPublico = ({ 
  usuario,
  cambiosTemporales,
  estiloMiniatura,
  estilosPerfil,
  tipografia,
  handleCambioTemporal,
  abrirModalNuevoEnlace,
  abrirModalNuevoTab,
  abrirModalEditarTab,
  eliminarTab,
  moverTab,
  abrirModalEdicionEnlace,
  eliminarEnlace,
  moverEnlace,
  onPhotoModalOpen,
  RenderizarCuentaBancaria,
  renderIcon
}) => {
  
  // Hook para obtener el usuario autenticado
  const { user } = useAuth()
  
  // Estado para el diseño seleccionado
  const [disenoSeleccionado, setDisenoSeleccionado] = useState(
    usuario.configuracionDisenos?.disenoGlobal || 'card'
  )

  // Efecto para sincronizar el diseño con las props del usuario
  useEffect(() => {
    const nuevoDiseño = usuario.configuracionDisenos?.disenoGlobal || 'card'
    if (nuevoDiseño !== disenoSeleccionado) {
      setDisenoSeleccionado(nuevoDiseño)
    }
  }, [usuario.configuracionDisenos?.disenoGlobal])

  // Función para manejar el cambio de diseño
  const handleCambioDiseño = (nuevoDiseño) => {
    setDisenoSeleccionado(nuevoDiseño)
    // Aquí podrías llamar a una función para actualizar la configuración en la base de datos
    console.log('Diseño cambiado a:', nuevoDiseño)
  }

  // Estados para manejar descripciones de tabs con auto-guardado
  const [descripcionesTabs, setDescripcionesTabs] = useState({})
  const [timersGuardado, setTimersGuardado] = useState({})
  const [guardandoTab, setGuardandoTab] = useState(null)

  // Efecto para inicializar las descripciones desde los tabs existentes
  useEffect(() => {
    if (cambiosTemporales.tabs) {
      const descripcionesIniciales = {}
      cambiosTemporales.tabs.forEach(tab => {
        descripcionesIniciales[tab.id] = tab.descripcion || ''
      })
      setDescripcionesTabs(descripcionesIniciales)
    }
  }, [cambiosTemporales.tabs])

  // Función para manejar cambios en descripciones con auto-guardado
  const handleCambioDescripcionTab = (tabId, nuevaDescripcion) => {
    // Actualizar el estado local inmediatamente
    setDescripcionesTabs(prev => ({
      ...prev,
      [tabId]: nuevaDescripcion
    }))

    // Cancelar timer anterior si existe
    if (timersGuardado[tabId]) {
      clearTimeout(timersGuardado[tabId])
    }

    // Crear nuevo timer para guardar después de 3 segundos
    const nuevoTimer = setTimeout(() => {
      guardarDescripcionTab(tabId, nuevaDescripcion)
    }, 3000)

    // Actualizar timers
    setTimersGuardado(prev => ({
      ...prev,
      [tabId]: nuevoTimer
    }))
  }

  // Función para guardar la descripción del tab
  const guardarDescripcionTab = async (tabId, descripcion) => {
    if (!user?.uid) {
      console.error('Usuario no autenticado')
      return
    }

    setGuardandoTab(tabId)
    
    try {
      console.log(`Guardando descripción del tab ${tabId}:`, descripcion)
      
      // Usar runTransaction para asegurar consistencia de datos
      await runTransaction(db, async (transaction) => {
        const userDocRef = doc(db, "usuarios", user.uid)
        const userDoc = await transaction.get(userDocRef)
        
        if (!userDoc.exists()) {
          throw new Error('Usuario no encontrado')
        }
        
        const userData = userDoc.data()
        const tabsActuales = userData.tabs || []
        const tabIndex = tabsActuales.findIndex(tab => tab.id === tabId)
        
        if (tabIndex === -1) {
          throw new Error(`Tab con ID ${tabId} no encontrado`)
        }
        
        // Crear una copia de los tabs actuales
        const nuevosTabs = [...tabsActuales]
        
        // Actualizar solo la descripción del tab específico
        nuevosTabs[tabIndex] = {
          ...nuevosTabs[tabIndex],
          descripcion: descripcion
        }
        
        // Actualizar el documento con los nuevos tabs
        transaction.update(userDocRef, {
          tabs: nuevosTabs
        })
        
        console.log('Descripción guardada exitosamente en Firebase')
        console.log('Tabs actualizados:', nuevosTabs)
        
        // Actualizar cambiosTemporales para sincronizar
        handleCambioTemporal('tabs', nuevosTabs)
      })
      
      // Delay artificial para hacer más visible el indicador de guardado
      await new Promise(resolve => setTimeout(resolve, 1500))
      
    } catch (error) {
      console.error('Error al guardar descripción:', error)
      // Aquí podrías mostrar un mensaje de error al usuario
    } finally {
      setGuardandoTab(null)
    }
  }
  
  // Clases para el estilo miniatura
  const clasesMiniatura = estiloMiniatura ? {
    container: "bg-gradient-to-br from-blue-50 to-purple-50 p-1 rounded-lg",
    card: "shadow-sm mb-2",
    cardBody: "p-2",
    avatar: "w-12 h-12 border-2 border-blue-500",
    title: "text-sm font-bold text-gray-800 mb-1",
    username: "text-blue-600 text-xs mb-1",
    bio: "text-gray-600 text-xs mb-1 leading-tight",
    info: "text-xs text-gray-600",
    linksTitle: "text-sm font-bold text-gray-800 mb-2 text-center",
    linkCard: "p-2 bg-white border border-gray-200 rounded-md hover:border-blue-500 transition-all duration-200 group mb-1",
    linkIcon: "w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center group-hover:bg-blue-200 transition-colors",
    linkIconText: "text-xs",
    linkTitle: "font-semibold text-gray-800 group-hover:text-blue-600 transition-colors text-xs leading-tight",
    linkDescription: "text-gray-600 text-xs mt-1 leading-tight",
    arrow: "text-gray-400 group-hover:text-blue-500 transition-colors text-xs"
  } : {
    container: "min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-8",
    card: "shadow-lg",
    cardBody: "p-8",
    avatar: "w-32 h-32 border-4 border-blue-500",
    title: "text-3xl font-bold text-gray-800 mb-2",
    username: "text-blue-600 text-lg mb-2",
    bio: "text-gray-600 mb-4 max-w-2xl",
    info: "text-gray-600",
    linksTitle: "text-2xl font-bold text-gray-800 mb-6 text-center",
    linkCard: "p-4 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all duration-200 group",
    linkIcon: "w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center group-hover:bg-blue-200 transition-colors",
    linkIconText: "text-xl",
    linkTitle: "font-semibold text-gray-800 group-hover:text-blue-600 transition-colors text-sm",
    linkDescription: "text-gray-600 text-sm mt-1",
    arrow: "text-gray-400 group-hover:text-blue-500 transition-colors"
  }

  return (
    <div className={estiloMiniatura ? "max-w-xs mx-auto" : "max-w-4xl mx-auto px-4"}>
      {/* Header del perfil - Modo Edición */}
      <Card 
        className={`mb-4 ${clasesMiniatura.card}`}
        style={{
          backgroundColor: estilosPerfil.fondoSecundario,
          borderColor: estilosPerfil.borde
        }}
      >
        <CardBody 
          className={clasesMiniatura.cardBody}
          style={{ color: estilosPerfil.texto }}
        >
          <div className={`flex ${estiloMiniatura ? 'flex-col' : 'flex-col md:flex-row'} items-center gap-${estiloMiniatura ? '3' : '6'}`}>
            {/* Avatar con opción de edición */}
            <div className="flex-shrink-0 relative">
              <Avatar
                src={usuario.fotoPerfil}
                size={estiloMiniatura ? "lg" : "2xl"}
                className={clasesMiniatura.avatar}
                name={usuario.nombre}
              />
              {!estiloMiniatura && (
                <Button
                  isIconOnly
                  size="sm"
                  color="primary"
                  className="absolute -bottom-2 -right-2 z-10"
                  onPress={onPhotoModalOpen}
                >
                  <i className="fas fa-camera text-xs"></i>
                </Button>
              )}
            </div>
            
            {/* Información del usuario - Editable */}
            <div className="flex-1 text-center md:text-left">
              {/* Nombre - Editable */}
              <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                {!estiloMiniatura ? (
                  <Input
                    value={cambiosTemporales.nombre}
                    onChange={(e) => handleCambioTemporal('nombre', e.target.value)}
                    placeholder="Tu nombre completo"
                    className="max-w-xs"
                    size="lg"
                    variant="underlined"
                    classNames={{
                      input: "text-xl font-bold text-center md:text-left",
                      inputWrapper: "border-b-2 border-blue-500"
                    }}
                  />
                ) : (
                  <h1 className={clasesMiniatura.title}>
                    {cambiosTemporales.nombre}
                  </h1>
                )}
              </div>
              
              <p className={clasesMiniatura.username}>
                @{usuario.nombreUsuario}
              </p>
              
              {/* Biografía - Editable */}
              <div className="mb-2">
                {!estiloMiniatura ? (
                  <Textarea
                    value={cambiosTemporales.bio}
                    onChange={(e) => handleCambioTemporal('bio', e.target.value)}
                    placeholder="Cuéntanos sobre ti..."
                    className="max-w-md"
                    variant="underlined"
                    minRows={2}
                    maxRows={4}
                    classNames={{
                      input: "text-center md:text-left",
                      inputWrapper: "border-b-2 border-blue-500"
                    }}
                  />
                ) : (
                  cambiosTemporales.bio && (
                    <p className={clasesMiniatura.bio}>
                      {cambiosTemporales.bio}
                    </p>
                  )
                )}
              </div>
              
              {/* Ubicación - Editable */}
              <div className={`flex items-center justify-center md:justify-start gap-2 ${estiloMiniatura ? 'mb-1' : 'mb-2'}`}>
                <span className="text-gray-500">📍</span>
                {!estiloMiniatura ? (
                  <Input
                    value={cambiosTemporales.ubicacion}
                    onChange={(e) => handleCambioTemporal('ubicacion', e.target.value)}
                    placeholder="Tu ciudad o país"
                    className="max-w-xs"
                    size="sm"
                    variant="underlined"
                    classNames={{
                      input: "text-center md:text-left",
                      inputWrapper: "border-b-2 border-blue-500"
                    }}
                  />
                ) : (
                  cambiosTemporales.ubicacion && (
                    <span className={clasesMiniatura.info}>{cambiosTemporales.ubicacion}</span>
                  )
                )}
              </div>
              
              {/* Sitio Web - Editable */}
              <div className="flex items-center justify-center md:justify-start gap-2">
                <span className="text-gray-500">🌐</span>
                {!estiloMiniatura ? (
                  <Input
                    value={cambiosTemporales.sitioWeb}
                    onChange={(e) => handleCambioTemporal('sitioWeb', e.target.value)}
                    placeholder="https://tu-sitio-web.com"
                    className="max-w-xs"
                    size="sm"
                    variant="underlined"
                    classNames={{
                      input: "text-center md:text-left",
                      inputWrapper: "border-b-2 border-blue-500"
                    }}
                  />
                ) : (
                  cambiosTemporales.sitioWeb && (
                    <a 
                      href={cambiosTemporales.sitioWeb} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={`text-blue-600 hover:text-blue-800 hover:underline ${clasesMiniatura.info}`}
                    >
                      {estiloMiniatura ? 'Sitio web' : cambiosTemporales.sitioWeb}
                    </a>
                  )
                )}
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      

      {/* Sección de enlaces - Modo Edición */}
      <Card 
        className={clasesMiniatura.card}
        style={{
          backgroundColor: estilosPerfil.fondoSecundario,
          borderColor: estilosPerfil.borde
        }}
      >
        <CardBody 
          className={clasesMiniatura.cardBody}
          style={{ color: estilosPerfil.texto }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className={clasesMiniatura.linksTitle}>
              Mis Enlaces
            </h2>
            <div className="flex items-center gap-4">
              {/* Selector de diseño - Solo visible cuando no es miniatura */}
              {!estiloMiniatura && (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">
                    Diseño:
                  </span>
                  <Select
                    size="sm"
                    variant="bordered"
                    placeholder="Selecciona un diseño"
                    aria-label="Selector de diseño de enlaces"
                    selectedKeys={[disenoSeleccionado]}
                    onSelectionChange={(keys) => {
                      const selectedKey = Array.from(keys)[0]
                      handleCambioDiseño(selectedKey)
                    }}
                    classNames={{
                      base: "w-44",
                      trigger: "h-8",
                    }}
                    renderValue={(items) => {
                      return items.map((item) => {
                        const diseño = diseñosDisponibles.find(d => d.id === item.key)
                        return (
                          <div key={item.key} className="flex items-center gap-2">
                            <span>{diseño?.icono}</span>
                            <span>{diseño?.nombre}</span>
                          </div>
                        )
                      })
                    }}
                  >
                    {diseñosDisponibles.map((diseño) => (
                      <SelectItem 
                        key={diseño.id} 
                        value={diseño.id}
                        textValue={diseño.nombre}
                      >
                        <div className="flex items-center gap-2">
                          <span>{diseño.icono}</span>
                          <div className="flex flex-col">
                            <span className="font-medium">{diseño.nombre}</span>
                            <span className="text-xs text-gray-500">{diseño.descripcion}</span>
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </Select>
                </div>
              )}
              
              <div className="flex gap-2">
                {!estiloMiniatura && (
                  <>
                    <Button
                      size="sm"
                      variant="flat"
                      onPress={abrirModalNuevoTab}
                      startContent={<i className="fas fa-folder-plus"></i>}
                      style={{
                        backgroundColor: estilosPerfil.secundario + '20',
                        color: estilosPerfil.secundario,
                        borderColor: estilosPerfil.secundario + '40'
                      }}
                    >
                      Nuevo Tab
                    </Button>
                    <Button
                      size="sm"
                      variant="flat"
                      onPress={abrirModalNuevoEnlace}
                      startContent={<i className="fas fa-plus"></i>}
                      style={{
                        backgroundColor: estilosPerfil.primario + '20',
                        color: estilosPerfil.primario,
                        borderColor: estilosPerfil.primario + '40'
                      }}
                    >
                      Añadir Enlace
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
          
          {/* Verificar si hay tabs */}
          {cambiosTemporales.tabs && cambiosTemporales.tabs.length > 0 ? (
            
              // Layout horizontal tradicional
              <Tabs 
                variant="underlined"
                className="w-full"
                size={estiloMiniatura ? "sm" : "md"}
                classNames={{
                  tabList: `gap-${estiloMiniatura ? '3' : '6'} w-full relative rounded-none p-0 border-b border-divider`,
                  cursor: "w-full bg-primary",
                  tab: "max-w-fit px-0 h-12",
                  tabContent: "group-data-[selected=true]:text-primary"
                }}
              >
              {cambiosTemporales.tabs.map((tab, tabIndex) => {
                const enlacesTab = cambiosTemporales.enlaces.filter(enlace => 
                  enlace.tab === tab.id || (!enlace.tab && tab.id === 'general')
                )
                
                return (
                  <Tab 
                    key={tab.id}
                    title={
                      <div className="flex items-center space-x-2">
                        <span className={estiloMiniatura ? 'text-sm' : ''}>{renderIcon(tab.icono)}</span>
                        <span className={estiloMiniatura ? 'text-sm' : ''}>{tab.nombre}</span>
                        <Chip size="sm" variant="flat" color="default">
                          {enlacesTab.length}
                        </Chip>
                        {!estiloMiniatura && (
                          <div className="flex gap-1 ml-2">
                            {/* Botones de reordenamiento */}
                            {tabIndex > 0 && (
                              <Button
                                isIconOnly
                                size="sm"
                                variant="light"
                                onPress={(e) => {
                                  moverTab(tabIndex, tabIndex - 1)
                                }}
                                title="Mover arriba"
                              >
                                <i className="fas fa-chevron-left text-xs"></i>
                              </Button>
                            )}
                            {tabIndex < cambiosTemporales.tabs.length - 1 && (
                              <Button
                                isIconOnly
                                size="sm"
                                variant="light"
                                onPress={(e) => {
                                  moverTab(tabIndex, tabIndex + 1)
                                }}
                                title="Mover abajo"
                              >
                                <i className="fas fa-chevron-right text-xs"></i>
                              </Button>
                            )}
                            <Button
                              isIconOnly
                              size="sm"
                              variant="light"
                              onPress={(e) => {
                                abrirModalEditarTab(tab, tabIndex)
                              }}
                            >
                              <i className="fas fa-edit text-xs"></i>
                            </Button>
                            <Button
                              isIconOnly
                              size="sm"
                              variant="light"
                              color="danger"
                              onPress={(e) => {
                                eliminarTab(tabIndex)
                              }}
                            >
                              <i className="fas fa-trash text-xs"></i>
                            </Button>
                          </div>
                        )}
                      </div>
                    }
                  >
                    <div className={`mt-${estiloMiniatura ? '2' : '6'}`}>
                      {/* Campo de descripción del tab - Solo visible cuando no es miniatura */}
                      {!estiloMiniatura && (
                        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-medium text-gray-700">
                              Descripción del tab "{tab.nombre}"
                            </label>
                            {guardandoTab === tab.id && (
                              <div className="flex items-center gap-2 text-xs text-blue-600">
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                                <span>Guardando...</span>
                              </div>
                            )}
                          </div>
                          <Textarea
                            value={descripcionesTabs[tab.id] || ''}
                            onChange={(e) => handleCambioDescripcionTab(tab.id, e.target.value)}
                            placeholder={`Describe el contenido de "${tab.nombre}"...`}
                            variant="bordered"
                            minRows={2}
                            maxRows={4}
                            classNames={{
                              input: "text-sm",
                              inputWrapper: "border-gray-300 focus-within:border-blue-500"
                            }}
                            aria-label={`Descripción del tab ${tab.nombre}`}
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Esta descripción se mostrará en la vista pública del perfil
                          </p>
                        </div>
                      )}
                      
                      <div className="space-y-4">
                        {enlacesTab.map((enlace, index) => {
                          const indexOriginal = cambiosTemporales.enlaces.indexOf(enlace)
                          return (
                            <ContenedorEnlace
                              key={enlace.id}
                              enlace={enlace}
                              mostrarBotones={true}
                              estiloMiniatura={estiloMiniatura}
                              puedeSubir={indexOriginal > 0}
                              puedeBajar={indexOriginal < cambiosTemporales.enlaces.length - 1}
                              onMoverArriba={() => moverEnlace(indexOriginal, indexOriginal - 1)}
                              onMoverAbajo={() => moverEnlace(indexOriginal, indexOriginal + 1)}
                              onEditar={() => abrirModalEdicionEnlace(enlace, indexOriginal)}
                              onEliminar={() => eliminarEnlace(indexOriginal)}
                            >
                              {enlace.esCuentaBancaria || enlace.tipo === 'cuenta_bancaria' ? (
                                <RenderizarCuentaBancaria
                                  enlace={enlace}
                                  showEditButtons={true}
                                  onEdit={() => abrirModalEdicionEnlace(enlace, indexOriginal)}
                                  onDelete={() => eliminarEnlace(indexOriginal)}
                                />
                              ) : (
                                <RenderizadorDiseño
                                  diseño={disenoSeleccionado}
                                  enlaces={[enlace]}
                                  estilosMiniatura={estiloMiniatura}
                                  esPreview={true}
                                  configuracionDiseño={usuario.configuracionDisenos?.configuraciones?.[disenoSeleccionado] || {}}
                                />
                              )}
                            </ContenedorEnlace>
                          )
                        })}
                      </div>
                    </div>
                  </Tab>
                )
              })}
            </Tabs>
            
          ) : (
            // Vista tradicional sin tabs - Modo Edición
            <div className="space-y-4">
              {cambiosTemporales.enlaces.map((enlace, index) => (
                <ContenedorEnlace
                  key={enlace.id}
                  enlace={enlace}
                  mostrarBotones={true}
                  estiloMiniatura={estiloMiniatura}
                  puedeSubir={index > 0}
                  puedeBajar={index < cambiosTemporales.enlaces.length - 1}
                  onMoverArriba={() => moverEnlace(index, index - 1)}
                  onMoverAbajo={() => moverEnlace(index, index + 1)}
                  onEditar={() => abrirModalEdicionEnlace(enlace, index)}
                  onEliminar={() => eliminarEnlace(index)}
                >
                  {enlace.esCuentaBancaria || enlace.tipo === 'cuenta_bancaria' ? (
                    <RenderizarCuentaBancaria
                      enlace={enlace}
                      showEditButtons={true}
                      onEdit={() => abrirModalEdicionEnlace(enlace, index)}
                      onDelete={() => eliminarEnlace(index)}
                    />
                  ) : (
                    <RenderizadorDiseño
                      diseño={disenoSeleccionado}
                      enlaces={[enlace]}
                      estilosMiniatura={estiloMiniatura}
                      esPreview={true}
                      configuracionDiseño={usuario.configuracionDisenos?.configuraciones?.[disenoSeleccionado] || {}}
                    />
                  )}
                </ContenedorEnlace>
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  )
}

export default EdicionPerfilPublico 